<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">정기권 대기 관리</h5>
      <div class="d-flex gap-2 align-items-center">
        <span class="text-muted small" v-if="selectedArea">총 {{ filtered.length }}건</span>
        <button
          v-if="selectedArea && filtered.length > 0"
          class="btn btn-primary"
          @click="openLotteryModal"
          :disabled="loadingLottery || loserMoveSaving"
        >
          {{ loadingLottery ? '추첨 중...' : (loserMoveSaving ? '그룹이동 중...' : '🎲 추첨') }}
        </button>
        <button
          v-if="selectedArea && filtered.length > 0"
          class="btn btn-outline-secondary"
          @click="openResetModal"
          :disabled="loadingReset || loserMoveSaving"
        >
          {{ loadingReset ? '초기화 중...' : (loserMoveSaving ? '그룹이동 중...' : '초기화') }}
        </button>
        <button
          v-if="selectedArea && filtered.length > 0"
          class="btn btn-outline-warning"
          @click="openBulkStatusModal"
          :disabled="getCheckedCount() === 0 || loserMoveSaving"
        >
          일괄 상태 변경
        </button>
        <button
          v-if="selectedArea && filtered.length > 0"
          class="btn btn-outline-info"
          @click="openMoveGroupModal"
          :disabled="getCheckedCount() === 0 || loadingLottery || loserMoveSaving"
        >
          그룹 이동 ({{ getCheckedCount() }}){{ loserMoveSaving ? ' - 잠금' : '' }}
        </button>
        <button
          v-if="selectedArea && filtered.length > 0"
          class="btn btn-outline-success"
          @click="openBulkSmsModal"
          :disabled="getCheckedCount() === 0 || loserMoveSaving"
        >
          🏧 카드 청구서 문자 ({{ getCheckedCount() }})
        </button>
        <button
          v-if="selectedArea && filtered.length > 0"
          class="btn btn-outline-warning"
          @click="openBulkCashSmsModal"
          :disabled="getCheckedCount() === 0 || loserMoveSaving"
        >
          💵 현금 청구서 문자 ({{ getCheckedCount() }})
        </button>
        <button
          v-if="selectedArea && filtered.length > 0"
          type="button"
          class="btn btn-outline-success"
          @click="exportToExcel"
        >
          📥 엑셀 다운로드
        </button>
      </div>
    </div>

    <!-- 검색/필터 -->
    <div class="row g-2 mb-3">
      <div class="col-auto">
        <select v-model="selectedArea" class="form-select form-select-sm" style="width: 220px;" @change="fetchWaiting">
          <option value="">-- 주차장 선택 --</option>
          <option v-for="a in areas" :key="a.AreaSeq" :value="a.AreaSeq">
            {{ a.AreaName }} ({{ a.AreaCode }})
          </option>
        </select>
      </div>
      <template v-if="selectedArea">
        <div class="col-auto">
          <select v-model="groupFilter" class="form-select form-select-sm" @change="onGroupFilterChange">
            <option value="">그룹 - 전체</option>
            <option value="NOGROUP">없음</option>
            <option v-for="g in groups" :key="g.GroupSeq" :value="g.GroupSeq">
              {{ g.GroupName }}
            </option>
          </select>
        </div>
        <div class="col-auto">
          <input
            v-model="search"
            type="text"
            class="form-control form-control-sm"
            placeholder="차량번호"
            style="width: 210px;"
          />
        </div>
        <div class="col-auto">
          <select v-model="statusFilter" class="form-select form-select-sm">
            <option value="">전체</option>
            <option value="WAITING">추첨대기</option>
            <option value="LOTTERY_COMPLETED">추첨완료</option>
            <option value="DocumentPending">증빙대기</option>
            <option value="CONFIRMED">확정</option>
            <option value="DISCOUNT">할인</option>
          </select>
        </div>
        <div class="col-auto">
          <select v-model="existingUserFilter" class="form-select form-select-sm">
            <option value="">신규/기존 - 전체</option>
            <option value="NEW">신규</option>
            <option value="EXISTING">기존</option>
          </select>
        </div>
        <div class="col-auto">
          <select v-model="discountFilter" class="form-select form-select-sm">
            <option value="">할인 - 전체</option>
            <option value="NONE">일반 (할인없음)</option>
            <option v-for="d in discounts" :key="d.DiscountSeq" :value="d.DiscountSeq">
              {{ d.DiscountName }} ({{ d.DiscountRate }}%)
            </option>
          </select>
        </div>
        <div class="col-auto ms-2">
          <div class="d-flex gap-3 align-items-center small">
            <label class="mb-0">
              <input type="radio" v-model="sortBy" value="waitingNumber" class="form-check-input me-1" />
              추첨순번 정렬
            </label>
            <label class="mb-0">
              <input type="radio" v-model="sortBy" value="applicationDate" class="form-check-input me-1" />
              신청일 정렬
            </label>
          </div>
        </div>
      </template>
    </div>

    <!-- 주차장 미선택 안내 -->
    <div v-if="!selectedArea" class="text-center text-muted py-5">
      주차장을 선택하면 해당 정기권 대기 목록이 표시됩니다.
    </div>

    <template v-else>
      <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>
      <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>

      <div v-else class="table-responsive">
        <table class="table table-hover align-middle table-sm">
          <thead class="table-dark">
            <tr>
              <th style="width: 40px">
                <input type="checkbox" class="form-check-input" v-model="selectAllChecked" @change="toggleCheckAll" />
              </th>
              <th style="width: 60px">#</th>
              <th>이름</th>
              <th>차량번호</th>
              <th style="width: 90px">전화번호</th>
              <th style="width: 80px" class="text-center">기존사용자</th>
              <th>요금제</th>
              <th>그룹</th>
              <th>할인</th>
              <th style="width: 70px" class="text-center">추첨순번</th>
              <th style="width: 80px" class="text-center">상태</th>
              <th>신청일</th>
              <th style="width: 100px" class="text-center">증빙서류</th>
              <th style="width: 90px" class="text-center">수정</th>
              <th style="width: 80px" class="text-center">관리</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filtered.length === 0">
              <td colspan="15" class="text-center text-muted py-4">해당하는 대기 신청이 없습니다.</td>
            </tr>
            <tr v-for="w in filtered" :key="w.WaitingSeq">
              <td class="text-center">
                <input type="checkbox" class="form-check-input" v-model="checked[w.WaitingSeq]" />
              </td>
              <td class="text-muted small">{{ w.WaitingSeq }}</td>
              <td>{{ w.UserName }}</td>
              <td class="font-monospace small">{{ w.CarNumber }}</td>
              <td class="small">{{ w.Tel }}</td>
              <td class="text-center">
                <span v-if="w.IsExistingUser" class="badge bg-success">기존</span>
                <span v-else class="badge bg-secondary">신규</span>
              </td>
              <td>{{ w.FeeName }}</td>
              <td>
                <span v-if="w.GroupName" class="badge bg-secondary">{{ w.GroupName }}</span>
                <span v-else class="text-muted small">-</span>
              </td>
              <td>{{ w.DiscountName || '없음' }}</td>
              <td class="text-center">
                <span v-if="w.WaitingNumber" :class="getWaitingNumberBadgeClass(w.WaitingNumber)" class="badge">
                  {{ w.WaitingNumber }}
                </span>
                <span v-else class="text-muted small">-</span>
              </td>
              <td class="text-center">
                <span :class="getStatusBadgeClass(w.Status)" class="badge text-dark" style="font-size:0.75rem;">
                  {{ getStatusLabel(w.Status) }}
                </span>
              </td>
              <td class="small text-muted">{{ formatDate(w.ApplicationDate) }}</td>
              <td class="text-center">
                <button v-if="w.FileCount > 0" class="btn btn-sm btn-outline-primary" @click="openFileViewer(w)" title="증빙서류 확인">
                  📎 {{ w.FileCount }}
                </button>
                <span v-else class="text-muted small">-</span>
              </td>
              <td class="text-center">
                <button class="btn btn-sm btn-outline-secondary" @click="openEditStatusModal(w)" title="상태 수정">✏️</button>
              </td>
              <td class="text-center">
                <div class="d-inline-flex align-items-center" style="gap:6px;">
                  <div class="d-inline-flex" role="group" aria-label="actions">
                    <button class="btn btn-sm btn-outline-primary" @click="openPaymentDetail(w)" title="청구서">📄</button>
                  </div>
                  <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(w)" style="min-width:56px;">삭제</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- 삭제 확인 모달 -->
    <div v-if="deleteModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <h6 class="fw-bold mb-2">대기 신청 삭제</h6>
          <p class="mb-3">
          <strong>{{ maskName(deleteTarget?.UserName) }}</strong> — 차량 <strong>{{ maskCarNumber(deleteTarget?.CarNumber) }}</strong><br/>
          <span class="text-muted small">주차장: {{ deleteTarget?.AreaName }}</span><br/>
          <span class="text-danger small">이 작업은 되돌릴 수 없습니다.</span>
        </p>
        <div v-if="deletePaymentRequests.length > 0" class="mb-3">
          <div class="mb-2"><strong>삭제될 청구서 목록</strong> (총 {{ deletePaymentRequests.length }}건)</div>
          <div class="table-responsive" style="max-height: 240px; overflow-y: auto;">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th class="py-2">청구번호</th>
                  <th class="py-2">청구명</th>
                  <th class="py-2">금액</th>
                  <th class="py-2">상태</th>
                  <th class="py-2">결제방식</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pm in deletePaymentRequests" :key="pm.PaymentSeq">
                  <td class="py-2 text-muted small">{{ pm.PaymentSeq }}</td>
                  <td class="py-2">{{ pm.BillName || '-' }}</td>
                  <td class="py-2">{{ pm.PaymentPrice?.toLocaleString() || 0 }}원</td>
                  <td class="py-2">
                    <span v-if="pm.Status === 'PAID'" class="badge bg-success">결제완료</span>
                    <span v-else-if="pm.Status === 'READY'" class="badge bg-info">결제전</span>
                    <span v-else class="badge bg-secondary">{{ pm.Status }}</span>
                  </td>
                  <td class="py-2">
                    <span v-if="pm.PaymentType === 'CARD'" class="badge bg-info">카드</span>
                    <span v-else-if="pm.PaymentType === 'CASH'" class="badge bg-warning">현금</span>
                    <span v-else class="badge bg-secondary">{{ pm.PaymentType || '-' }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="alert alert-light py-2 mb-3 small text-muted">
          이 신청건에 연결된 청구서가 없습니다.
        </div>
        <div v-if="deleteError" :class="deleteError.includes('운영 중 정기권') ? 'alert alert-warning py-1 small' : 'alert alert-danger py-1 small'">
          {{ deleteError }}
        </div>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="deleteModal = false">취소</button>
          <button class="btn btn-danger btn-sm" :disabled="saving" @click="doDelete">
            {{ saving ? '삭제 중...' : '삭제' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 초기화(추첨 리셋) 확인 모달 -->
    <div v-if="resetModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <h6 class="fw-bold mb-2">추첨 초기화</h6>
        <p class="mb-3">
          선택한 주차장(<strong>{{ getSelectedAreaName() }}</strong>)의 추첨 결과를 초기화합니다.<br/>
          모든 신청자의 <code>추첨순번(WaitingNumber)</code>이 초기화됩니다.
        </p>
        <div v-if="resetError" class="alert alert-danger py-1 small">{{ resetError }}</div>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="resetModal = false">취소</button>
          <button class="btn btn-danger btn-sm" :disabled="loadingReset" @click="doReset">{{ loadingReset ? '초기화 중...' : '초기화' }}</button>
        </div>
      </div>
    </div>

    <!-- 일괄 상태 변경 모달 -->
    <div v-if="bulkStatusModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 420px;" @click.stop>
        <h6 class="fw-bold mb-3">일괄 상태 변경</h6>
        <p class="small text-muted mb-2">선택된 {{ getCheckedCount() }}건의 상태를 일괄 변경합니다.</p>
        <div class="mb-3">
          <label class="form-label small fw-bold">변경할 상태 <span class="text-danger">*</span></label>
          <select v-model="bulkStatus" class="form-select form-select-sm">
            <option value="">-- 상태 선택 --</option>
            <option value="WAITING">추첨대기</option>
            <option value="LOTTERY_COMPLETED">추첨완료</option>
            <option value="DocumentPending">증빙대기</option>
            <option value="SENDED_SMS">문자발송</option>
            <option value="PAYMENT_COMPLTETED">결제완료</option>
          </select>
        </div>
        <div v-if="bulkStatusError" class="alert alert-danger py-1 small mb-2">{{ bulkStatusError }}</div>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="bulkStatusModal = false">취소</button>
          <button class="btn btn-primary btn-sm" :disabled="bulkStatusSaving || !bulkStatus" @click="doBulkStatusChange">
            {{ bulkStatusSaving ? '변경 중...' : '변경' }}
          </button>
        </div>
      </div>
    </div>

    <!-- SMS 발송 모달 -->
    <div v-if="smsModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 600px;" @click.stop>
        <h6 class="fw-bold mb-3">📱 문자 발송 및 결제 요청</h6>
        
        <!-- 신청자 정보 -->
        <div class="bg-light p-3 rounded mb-3" style="font-size: 0.9rem;">
          <div class="row g-2">
            <div class="col-6">
              <strong>사용자:</strong> {{ maskName(smsTarget?.UserName) }}
            </div>
            <div class="col-6">
              <strong>전화:</strong> {{ maskTel(smsTarget?.Tel) }}
            </div>
            <div class="col-6">
              <strong>차량:</strong> {{ smsTarget?.CarNumber }}
            </div>
            <div class="col-6">
              <strong>주차장:</strong> {{ smsTarget?.AreaName }}
            </div>
            <div class="col-6">
              <strong>요금제:</strong> {{ smsTarget?.FeeName }}
            </div>
          </div>
        </div>

        <!-- 요금 계산 표시 -->
        <div class="alert alert-info py-2 mb-3" style="font-size: 0.85rem;">
          <strong>결제 금액:</strong><br/>
          <span v-if="smsTarget?.DiscountRate">
            ₩{{ smsTarget?.Price?.toLocaleString() }} - ₩{{ (smsTarget?.Price - calculateAmount(smsTarget)).toLocaleString() }}({{ smsTarget?.DiscountRate }}%) = <strong style="color:red;">₩{{ calculateAmount(smsTarget).toLocaleString() }}</strong>
          </span>
          <span v-else>
            <strong style="color:red;">₩{{ calculateAmount(smsTarget).toLocaleString() }}</strong>
          </span>
        </div>

        <!-- 이용 기간 설정 -->
        <div class="mb-3">
          <label class="form-label small fw-bold">이용 기간</label>
          <div class="row g-2">
            <div class="col-6">
              <label class="form-label small">시작일</label>
              <input v-model="smsPaymentData.startDate" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-6">
              <label class="form-label small">종료일</label>
              <input v-model="smsPaymentData.endDate" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-12 mt-2">
              <label class="form-label small">결제기한</label>
              <input v-model="smsPaymentData.expireDate" type="datetime-local" class="form-control form-control-sm" />
            </div>
          </div>
        </div>

        <!-- 메시지 입력 -->
        <div class="mb-3">
          <label class="form-label small fw-bold">발송 메시지</label>
          <textarea v-model="smsMessage" class="form-control" rows="4" style="font-size: 0.9rem;"></textarea>
        </div>

        <div v-if="smsError" class="alert alert-danger mb-3">
          <h6 class="fw-bold text-danger mb-2">❌ 오류</h6>
          <div style="font-size: 0.85rem; white-space: pre-wrap; word-break: break-word; background: #fff; padding: 8px; border-radius: 4px; border-left: 3px solid #dc3545; max-height: 200px; overflow-y: auto;">{{ smsError }}</div>
        </div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="smsModal = false">취소</button>
          <button class="btn btn-primary btn-sm" :disabled="smsSending" @click="sendSms">
            {{ smsSending ? '발송 중...' : '문자 발송' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 카드 청구서 문자 발송 모달 컴포넌트 -->
    <CardInvoiceSmsModal
      :isOpen="cardSmsModalOpen"
      :selectedItems="filtered.filter(w => checked[w.WaitingSeq])"
      :areas="areas"
      :selectedAreaSeq="selectedArea"
      @close="cardSmsModalOpen = false"
      @bulk-sms-sent="onCardSmsSent"
    />

    <!-- 현금 청구서 문자 발송 모달 컴포넌트 -->
    <CashInvoiceSmsModal
      :isOpen="cashSmsModalOpen"
      :selectedItems="filtered.filter(w => checked[w.WaitingSeq])"
      :areas="areas"
      :selectedAreaSeq="selectedArea"
      @close="cashSmsModalOpen = false"
      @bulk-sms-sent="onCashSmsSent"
    />

    <!-- 결제 완료 모달 -->
    <div v-if="paymentModal" class="modal-backdrop-custom">
      <div class="modal-box" @click.stop>
        <h6 class="fw-bold mb-3">💳 결제 완료 처리</h6>
        
        <div class="bg-light p-3 rounded mb-3" style="font-size: 0.9rem;">
          <div class="row g-2">
            <div class="col-6">
              <strong>사용자:</strong> {{ maskName(paymentTarget?.UserName) }}
            </div>
            <div class="col-6">
              <strong>차량:</strong> {{ maskCarNumber(paymentTarget?.CarNumber) }}
            </div>
            <div class="col-12">
              <strong>주차장:</strong> {{ paymentTarget?.AreaName }} — {{ paymentTarget?.FeeName }}
            </div>
          </div>
        </div>

        <p class="mb-3">
          이 신청건의 상태를 <strong style="color: green;">결제완료</strong>로 업데이트합니다.
        </p>

        <div v-if="paymentError" class="alert alert-danger py-1 small mb-2">{{ paymentError }}</div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="paymentModal = false">취소</button>
          <button class="btn btn-success btn-sm" :disabled="paymentProcessing" @click="completePayment">
            {{ paymentProcessing ? '처리 중...' : '결제 완료' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 청구서 상세 모달 (컴포넌트) -->
    <PaymentDetailModal
      :isOpen="paymentDetailModal"
      :target="paymentDetailTarget"
      :payments="paymentDetailsList"
      :processing="paymentDetailProcessing"
      :error="paymentDetailError"
      @close="closePaymentDetail"
      @complete-payment="completePaymentManually"
      @delete-payment="deletePaymentRequest"
    />

    <!-- 상태 수정 모달 -->
    <div v-if="editStatusModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 550px;" @click.stop>
        <h6 class="fw-bold mb-3">항목 수정</h6>
        <div class="mb-3">
          <label class="form-label small fw-bold">현재 상태</label>
          <div class="form-control form-control-sm mb-2">{{ getStatusLabel(editTarget?.Status) || '-' }}</div>
          <label class="form-label small fw-bold">변경할 상태</label>
          <select v-model="editStatus" class="form-select form-select-sm">
            <option value="">선택</option>
            <option value="WAITING">추첨대기</option>
            <option value="LOTTERY_COMPLETED">추첨완료</option>
            <option value="DocumentPending">증빙대기</option>
            <option value="SENDED_SMS">문자발송</option>
            <option value="PAYMENT_COMPLTETED">결제완료</option>
          </select>
        </div>
        <div class="mb-3">
          <label class="form-label small fw-bold">현재 요금제</label>
          <div class="form-control form-control-sm mb-2">{{ editTarget?.FeeName || '-' }}</div>
          <label class="form-label small fw-bold">변경할 요금제</label>
          <select v-model="editFeeSeq" class="form-select form-select-sm">
            <option value="">-- 변경 안 함 --</option>
            <option v-for="f in fees" :key="f.FeeSeq" :value="f.FeeSeq">
              {{ f.FeeName }} ({{ f.Price?.toLocaleString() || 0 }}원)
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="form-label small fw-bold">현재 할인</label>
          <div class="form-control form-control-sm mb-2">{{ editTarget?.DiscountName || '없음' }}</div>
          <label class="form-label small fw-bold">변경할 할인</label>
          <select v-model="editDiscountSeq" class="form-select form-select-sm">
            <option value="">-- 할인 없음 --</option>
            <option v-for="d in discounts" :key="d.DiscountSeq" :value="d.DiscountSeq">
              {{ d.DiscountName }} ({{ d.DiscountRate }}%)
            </option>
          </select>
        </div>
        <div class="mb-3">
          <label class="form-label small fw-bold">현재 신규/기존</label>
          <div class="form-control form-control-sm mb-2">
            <span v-if="editTarget?.IsExistingUser" class="badge bg-success">기존</span>
            <span v-else class="badge bg-secondary">신규</span>
          </div>
          <label class="form-label small fw-bold">변경할 신규/기존</label>
          <select v-model="editIsExistingUser" class="form-select form-select-sm">
            <option value="">-- 변경 안 함 --</option>
            <option :value="true">기존</option>
            <option :value="false">신규</option>
          </select>
        </div>
        <div v-if="editError" class="alert alert-danger py-1 small mb-2">{{ editError }}</div>
        <div class="d-flex gap-2 justify-content-between mb-3">
          <button class="btn btn-outline-info btn-sm" @click="openFileUploadModal(editTarget)">
            📎 증빙서류 수정
          </button>
          <div></div>
        </div>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="editStatusModal = false">취소</button>
          <button class="btn btn-primary btn-sm" :disabled="editSaving" @click="saveEditStatus">
            {{ editSaving ? '저장 중...' : '저장' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 추첨 모달 -->
    <div v-if="lotteryModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 600px; max-height: 90vh; overflow-y: auto;" @click.stop>
        <h6 class="fw-bold mb-3">정기권 대기 추첨 - {{ getSelectedAreaName() }}</h6>
        <div class="alert alert-light border small text-muted mb-3 py-2">
          상태가 <strong>추첨대기</strong>인 신청자만 추첨 대상에 포함됩니다.<br>
          또한 <strong>그룹이 지정된 대상</strong>만 추첨 대상에 포함됩니다.
        </div>
        
        <div v-if="lotteryGroups.length === 0" class="text-center text-muted py-4">
          추첨 대기 그룹이 없습니다.
        </div>

        <template v-else>
          <!-- 그룹별 추첨 상태 -->
          <div class="mb-3">
            <div v-for="group in lotteryGroups" :key="group.GroupSeq" class="card mb-2">
              <div class="card-body p-3">
                <div class="row align-items-center g-2">
                  <div class="col-auto">
                    <strong class="small">{{ group.GroupName }}</strong>
                  </div>
                  <div class="col">
                    <small class="text-muted">
                      수용: <span class="fw-bold">{{ group.QuotaCount }}</span>명 | 
                      대기: <span class="fw-bold">{{ group.candidateCount }}</span>명
                    </small>
                  </div>
                  <div class="col-auto d-flex gap-2 align-items-center">
                    <!-- 대기자 확인 버튼 -->
                    <button 
                      v-if="(group.status === 'idle' || group.status === 'error') && group.candidateCount > 0"
                      class="btn btn-sm btn-outline-info"
                      @click="showWaitingList(group)"
                      :disabled="waitingListLoading"
                    >
                      📋 대기자 확인
                    </button>

                    <!-- 추첨 버튼 (미진행 상태일 때만 활성화) -->
                    <button 
                      v-if="group.status === 'idle' || group.status === 'error'"
                      class="btn btn-sm btn-primary"
                      @click="doGroupLottery(group)"
                      :disabled="group.isProcessing || group.candidateCount === 0"
                    >
                      {{ group.isProcessing ? '추첨 중...' : group.candidateCount === 0 ? '대기 0명' : '🎲 추첨' }}
                    </button>
                    
                    <!-- 당첨자 확인 버튼 (완료 상태일 때만) -->
                    <button 
                      v-if="group.status === 'completed' && group.candidateCount > 0"
                      class="btn btn-sm btn-outline-primary"
                      @click="showLotteryWinners(group)"
                      :disabled="lotteryWinnersLoading"
                    >
                      👥 당첨자 확인
                    </button>
                    
                    <!-- 미당첨자 그룹 이동 버튼 (완료 상태일 때만) -->
                    <button 
                      v-if="group.status === 'completed' && group.candidateCount > 0"
                      class="btn btn-sm btn-outline-warning"
                      @click="openLoserMoveModalFromLottery(group)"
                      :disabled="lotteryWinnersLoading"
                    >
                      👤 미당첨자 그룹 이동
                    </button>
                    
                    <span v-else-if="group.status === 'completed' && group.candidateCount === 0" class="text-muted small">
                      대기자 없음
                    </span>
                  </div>
                </div>
                
                <!-- 진행바 -->
                <div v-if="group.status !== 'idle'" class="mt-2">
                  <div class="progress" style="height: 20px;">
                    <div
                      class="progress-bar"
                      :class="{
                        'bg-success': group.status === 'completed',
                        'bg-warning': group.status === 'skipped',
                        'bg-danger': group.status === 'error'
                      }"
                      :style="{ width: (group.status === 'skipped' ? 100 : group.progress) + '%' }"
                      role="progressbar"
                    >
                      <span v-if="group.progress > 10 && group.status !== 'skipped'" style="font-size: 0.7rem;">{{ group.progress }}%</span>
                    </div>
                  </div>
                </div>

                <!-- 에러 메시지 -->
                <div v-if="group.error" class="alert alert-danger py-1 mt-2 mb-0 small">
                  {{ group.error }}
                </div>

                <!-- 상태 표시 -->
                <div v-if="group.status === 'processing'" class="mt-2 small text-muted">
                  추첨 중...
                </div>
                <div v-else-if="group.status === 'completed'" class="mt-2 small text-success">
                  ✓ 추첨 완료
                </div>
                <div v-else-if="group.status === 'skipped'" class="mt-2 small text-warning">
                  ⊘ 스킵 (대기자 없음)
                </div>
              </div>
            </div>
          </div>

          <div v-if="lotteryError" class="alert alert-danger py-2 small mb-3">{{ lotteryError }}</div>
          <div v-if="lotterySuccess" class="alert alert-success py-2 small mb-3">{{ lotterySuccess }}</div>
        </template>

        <div class="d-flex gap-2 justify-content-end">
          <button 
            class="btn btn-secondary btn-sm" 
            @click="closeLotteryModal"
          >
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 당첨자 리스트 모달 -->
    <div v-if="lotteryWinnersModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 700px; max-height: 80vh; overflow-y: auto;" @click.stop>
        <h6 class="fw-bold mb-3">당첨자 리스트</h6>
        
        <div v-if="selectedLotteryGroupForWinners" class="alert alert-info py-2 mb-3 small">
          <strong>{{ selectedLotteryGroupForWinners.GroupName }}</strong> | 
          수용대수: <strong>{{ selectedLotteryGroupForWinners.QuotaCount }}</strong>명
        </div>

        <div v-if="lotteryWinnersLoading" class="text-center text-muted py-4">
          로드 중...
        </div>

        <div v-else-if="lotteryWinnersError" class="alert alert-danger py-2 small mb-3">
          {{ lotteryWinnersError }}
        </div>

        <div v-else-if="lotteryWinners.length === 0" class="text-center text-muted py-4">
          당첨자가 없습니다.
        </div>

        <template v-else>
          <div class="table-responsive">
            <table class="table table-sm table-hover">
              <thead class="table-light">
                <tr>
                  <th style="width: 50px">#</th>
                  <th>이름</th>
                  <th>차량번호</th>
                  <th style="width: 80px">추첨순번</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="winner in lotteryWinners" :key="winner.WaitingSeq">
                  <td class="text-muted small">{{ winner.WaitingSeq }}</td>
                  <td>{{ maskName(winner.UserName) }}</td>
                  <td class="font-monospace small">{{ maskCarNumber(winner.CarNumber) }}</td>
                  <td class="text-center">
                    <span class="badge bg-success">{{ winner.WaitingNumber }}</span>
                  </td>
                  <td>
                    <span class="badge" :class="getStatusBadgeClass(winner.Status)">
                      {{ getStatusLabel(winner.Status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div class="d-flex gap-2 justify-content-end mt-3">
          <button class="btn btn-secondary btn-sm" @click="lotteryWinnersModal = false">
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 그룹 이동 모달 -->
    <div v-if="moveGroupModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 400px;" @click.stop>
        <h6 class="fw-bold mb-3">그룹 이동</h6>
        
        <div class="bg-light p-3 rounded mb-3" style="font-size: 0.9rem;">
          <strong>선택 인원:</strong> {{ getCheckedCount() }}명
        </div>

        <div class="mb-3">
          <label class="form-label small fw-bold">이동할 그룹 선택 <span class="text-danger">*</span></label>
          <select v-model="selectedMoveGroup" class="form-select form-select-sm">
            <option value="">-- 그룹 선택 --</option>
            <option v-for="g in groups" :key="g.GroupSeq" :value="g.GroupSeq">
              {{ g.GroupName }}
            </option>
          </select>
        </div>

        <div v-if="moveGroupError" class="alert alert-danger py-1 small mb-2">{{ moveGroupError }}</div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="moveGroupModal = false">취소</button>
          <button class="btn btn-primary btn-sm" :disabled="moveGroupSaving || !selectedMoveGroup" @click="doMoveGroup">
            {{ moveGroupSaving ? '이동 중...' : '이동' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 대기자 리스트 모달 -->
    <div v-if="waitingListModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 700px; max-height: 80vh; overflow-y: auto;" @click.stop>
        <h6 class="fw-bold mb-3">대기자 리스트</h6>
        
        <div v-if="selectedGroupForWaitingList" class="alert alert-info py-2 mb-3 small">
          <strong>{{ selectedGroupForWaitingList.GroupName }}</strong> 그룹 | 
          대기자: <strong>{{ waitingList.length }}</strong>명
        </div>

        <div v-if="waitingListLoading" class="text-center text-muted py-4">
          로드 중...
        </div>

        <div v-else-if="waitingListError" class="alert alert-danger py-2 small mb-3">
          {{ waitingListError }}
        </div>

        <div v-else-if="waitingList.length === 0" class="text-center text-muted py-4">
          대기자가 없습니다.
        </div>

        <template v-else>
          <div class="table-responsive" style="max-height: 350px; border: 1px solid #dee2e6; overflow-y: auto;">
            <table class="table table-sm table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th style="width: 50px">#</th>
                  <th>이름</th>
                  <th>차량번호</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(waiter, idx) in waitingList" :key="waiter.WaitingSeq">
                  <td class="text-muted small">{{ idx + 1 }}</td>
                  <td>{{ maskName(waiter.UserName) }}</td>
                  <td class="font-monospace small">{{ maskCarNumber(waiter.CarNumber) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div class="d-flex gap-2 justify-content-end mt-3">
          <button class="btn btn-secondary btn-sm" @click="waitingListModal = false">
            닫기
          </button>
        </div>
      </div>
    </div>

    <!-- 미당첨자 그룹 이동 모달 -->
    <div v-if="loserMoveModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 700px; max-height: 80vh; overflow-y: auto;" @click.stop>
        <h6 class="fw-bold mb-3">미당첨자 그룹 이동</h6>
        
        <div v-if="selectedGroupForLoserMove" class="alert alert-info py-2 mb-3 small">
          <strong>{{ selectedGroupForLoserMove.GroupName }}</strong> 그룹에서 미당첨된 인원
        </div>

        <div v-if="loserMoveLoading" class="text-center text-muted py-4">
          로드 중...
        </div>

        <div v-else-if="loserMoveSaving" class="alert alert-info py-2 mb-3">
          <div class="d-flex align-items-center gap-2 mb-2">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <strong>그룹 이동 중...</strong>
          </div>
          <div class="progress" style="height: 20px;">
            <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" 
                 style="width: 100%; line-height: 20px; font-size: 12px;">
              처리 중
            </div>
          </div>
          <small class="text-muted mt-2 d-block">{{ selectedLoserSeqs.length }}명의 미당첨자를 이동하고 있습니다...</small>
        </div>

        <div v-else-if="loserMoveError" class="alert alert-danger py-2 small mb-3">
          {{ loserMoveError }}
        </div>

        <div v-else-if="losersToMove && losersToMove.length === 0" class="text-center text-muted py-4">
          미당첨자가 없습니다.
        </div>

        <template v-else>
          <!-- 필터 -->
          <div class="mb-3 pb-3 border-bottom">
            <label class="form-label small fw-bold">사용자 유형 필터</label>
            <div class="d-flex gap-3">
              <div class="form-check">
                <input 
                  id="filterNew" 
                  v-model="filterNewUser" 
                  type="checkbox" 
                  class="form-check-input"
                />
                <label for="filterNew" class="form-check-label small">신규</label>
              </div>
              <div class="form-check">
                <input 
                  id="filterExisting" 
                  v-model="filterExistingUser" 
                  type="checkbox" 
                  class="form-check-input"
                />
                <label for="filterExisting" class="form-check-label small">기존</label>
              </div>
            </div>
          </div>

          <!-- 미당첨자 리스트 -->
          <div class="mb-3">
            <label class="form-label small fw-bold">
              <input 
                v-model="selectAllLoserChecked" 
                type="checkbox" 
                class="form-check-input me-2"
              />
              미당첨자 리스트 ({{ losersToMove?.length || 0 }}명)
            </label>
            <div class="table-responsive" style="max-height: 350px; border: 1px solid #dee2e6;">
              <table class="table table-sm table-hover mb-0">
                <thead class="table-light sticky-top">
                  <tr>
                    <th style="width: 40px; text-align: center;">
                      <input 
                        v-model="selectAllLoserChecked" 
                        type="checkbox"
                      />
                    </th>
                    <th>이름</th>
                    <th>차량번호</th>
                    <th style="width: 100px;">유형</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="loser in losersToMove" :key="loser.WaitingSeq">
                    <td class="text-center" style="vertical-align: middle;">
                      <input 
                        :checked="selectedLoserSeqs.includes(loser.WaitingSeq)"
                        type="checkbox"
                        @change="(e) => {
                          if (e.target.checked) {
                            if (!selectedLoserSeqs.includes(loser.WaitingSeq)) {
                              selectedLoserSeqs.push(loser.WaitingSeq)
                            }
                          } else {
                            const idx = selectedLoserSeqs.indexOf(loser.WaitingSeq)
                            if (idx > -1) {
                              selectedLoserSeqs.splice(idx, 1)
                            }
                          }
                        }"
                      />
                    </td>
                    <td>{{ maskName(loser.UserName) }}</td>
                    <td class="font-monospace small">{{ maskCarNumber(loser.CarNumber) }}</td>
                    <td>
                      <span v-if="loser.IsExistingUser" class="badge bg-info small">기존</span>
                      <span v-else class="badge bg-warning small">신규</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- 이동할 그룹 선택 -->
          <div class="mb-3">
            <label class="form-label small fw-bold">이동할 그룹 선택 <span class="text-danger">*</span></label>
            <select v-model="selectedLoserMoveGroup" class="form-select form-select-sm">
              <option value="">-- 그룹 선택 --</option>
              <option 
                v-for="g in groups" 
                :key="g.GroupSeq" 
                :value="g.GroupSeq"
                :disabled="g.GroupSeq === selectedGroupForLoserMove.GroupSeq"
              >
                {{ g.GroupName }} {{ g.GroupSeq === selectedGroupForLoserMove.GroupSeq ? '(현재 그룹)' : '' }}
              </option>
            </select>
          </div>

          <div v-if="loserMoveError" class="alert alert-danger py-2 small mb-3">{{ loserMoveError }}</div>
        </template>

        <div class="d-flex gap-2 justify-content-end">
          <button 
            class="btn btn-secondary btn-sm" 
            @click="loserMoveModal = false"
          >
            취소
          </button>
          <button 
            v-if="losersToMove && losersToMove.length > 0"
            class="btn btn-primary btn-sm" 
            :disabled="loserMoveSaving || !selectedLoserMoveGroup || selectedLoserSeqs.length === 0"
            @click="doMoveLoserGroup"
          >
            {{ loserMoveSaving ? '이동 중...' : `그룹 이동 (${selectedLoserSeqs.length}명)` }}
          </button>
        </div>
      </div>
    </div>

    <!-- 파일 업로드 모달 (관리자용) -->
    <div v-if="fileUploadModal" class="modal-backdrop-custom">
      <div class="modal-box" style="max-width: 600px; max-height: 90vh; overflow-y: auto;" @click.stop>
        <h6 class="fw-bold mb-3">증빙서류 업로드</h6>
        
          <div v-if="editTarget" class="alert alert-info py-2 mb-3 small">
          <strong>{{ maskName(editTarget.UserName) }}</strong> - {{ maskCarNumber(editTarget.CarNumber) }}
        </div>

        <!-- 드래그 드롭 영역 -->
        <div
          class="drop-area mb-3"
          @drop.prevent="onDrop"
          @dragover.prevent
          @click="triggerFileInput"
        >
          <input ref="fileInput" type="file" multiple @change="onInputFiles" class="d-none" />
          <div class="text-center p-3">
            <p class="mb-1 small">파일을 여기에 끌어다 놓거나 클릭해서 선택하세요</p>
            <small class="text-muted">최대 5개, 각 10MB 이하</small>
          </div>
        </div>

        <!-- 파일 목록 -->
        <div v-if="editingFiles.length" class="mb-3">
          <div class="file-list">
            <div v-for="f in editingFiles" :key="f.id" class="file-item d-flex align-items-center mb-2">
              <div class="thumb me-2" v-if="f.preview">
                <img :src="f.preview" alt="preview" />
              </div>
              <div class="flex-grow-1">
                <div class="d-flex justify-content-between">
                  <div>
                    <strong class="small">{{ f.file.name }}</strong>
                    <div class="text-muted small">{{ formatBytes(f.file.size) }}</div>
                  </div>
                </div>

                <div class="progress mt-2" style="height:6px" v-if="f.progress > 0">
                  <div class="progress-bar" :style="{ width: f.progress + '%' }"></div>
                </div>

                <div class="small text-muted mt-1">
                  <span v-if="f.status === 'pending'">대기중</span>
                  <span v-if="f.status === 'uploading'">전송중...</span>
                  <span v-if="f.status === 'done'" class="text-success">완료</span>
                  <span v-if="f.status === 'error'" class="text-danger">오류</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="fileUploadError" class="alert alert-danger py-2 small mb-3">{{ fileUploadError }}</div>

        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-secondary btn-sm" @click="closeFileUploadModal" :disabled="fileUploading">
            취소
          </button>
          <button 
            class="btn btn-primary btn-sm" 
            @click="uploadEditingFiles" 
            :disabled="fileUploading || editingFiles.length === 0"
          >
            {{ fileUploading ? '업로드 중...' : '업로드' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 증빙서류 뷰어 모달 -->
    <FileViewerModal
      :isOpen="fileViewerModal"
      :waitingSeq="fileViewerWaitingSeq"
      :files="viewerFiles"
      @close="fileViewerModal = false"
      @status-updated="onEvidenceStatusUpdated"
    />
  </div>
</template>

<script>
import axios from 'axios'
import FileViewerModal from '../../components/FileViewerModal.vue'
import CardInvoiceSmsModal from '../../components/CardInvoiceSmsModal.vue'
import CashInvoiceSmsModal from '../../components/CashInvoiceSmsModal.vue'
import PaymentDetailModal from '../../components/PaymentDetailModal.vue'

export default {
  name: 'AdminParkingPassWaitingView',
  components: { FileViewerModal, CardInvoiceSmsModal, CashInvoiceSmsModal, PaymentDetailModal },
  data() {
    return {
      areas: [],
      selectedArea: '',
      waitings: [],
      loading: false,
      loadError: null,
      search: '',
      statusFilter: '',
      existingUserFilter: '',
      discountFilter: '',
      groupFilter: '',
      discounts: [],
      fees: [],
      groups: [],
      deleteModal: false,
      deleteTarget: null,
      deleteError: null,
      deletePaymentRequests: [],
      saving: false,
      // 초기화 (추첨 리셋)
      resetModal: false,
      loadingReset: false,
      resetError: null,
      // SMS 발송
      smsModal: false,
      smsTarget: null,
      smsMessage: '',
      createdPayment: null,
      smsBillName: '',
      smsSending: false,
      smsError: null,
      smsPaymentData: {
        startDate: '',
        endDate: '',
        expireDate: ''
      },
      // 결제 완료
      paymentModal: false,
      paymentTarget: null,
      paymentProcessing: false,
      paymentError: null,
      // 청구서 조회 및 수동 완료
      paymentDetailModal: false,
      paymentDetailTarget: null,
      paymentDetailsList: [],
      paymentDetailProcessing: false,
      paymentDetailError: null,
      // 수정 모달
      editStatusModal: false,
      editTarget: null,
      editStatus: '',
      editFeeSeq: '',
      editDiscountSeq: '',
      editIsExistingUser: '',
      editError: null,
      editSaving: false,
      // 추첨 관련
      lotteryModal: false,
      loadingLottery: false,
      lotteryGroups: [], // [{ GroupSeq, GroupName, QuotaCount, candidateCount, progress: 0, status: 'idle', error: null, winners: [], isProcessing: false }]
      lotteryError: null,
      lotterySuccess: null,
      // 당첨자 조회
      lotteryWinnersModal: false,
      lotteryWinners: [],
      selectedLotteryGroupForWinners: null,
      lotteryWinnersLoading: false,
      lotteryWinnersError: null,
      // 대기자 리스트 조회
      waitingListModal: false,
      waitingList: [],
      selectedGroupForWaitingList: null,
      waitingListLoading: false,
      waitingListError: null,
      // 미당첨자 그룹 이동
      loserMoveModal: false,
      losersToMove: [],
      selectedGroupForLoserMove: null,
      selectedLoserMoveGroup: '',
      loserMoveLoading: false,
      loserMoveSaving: false,
      loserMoveError: null,
      filterNewUser: false,
      filterExistingUser: true,
      selectedLoserSeqs: [],
      sortBy: 'waitingNumber',
      // 파일 뷰어
      fileViewerModal: false,
      fileViewerWaitingSeq: null,
      viewerFiles: [],
      fileViewerLoading: false,
      // 그룹 이동
      checked: {},
      moveGroupModal: false,
      selectedMoveGroup: '',
      moveGroupError: null,
      moveGroupSaving: false,
      // 카드 청구서 문자 발송
      cardSmsModalOpen: false,
      // 현금 청구서 문자 발송
      cashSmsModalOpen: false,
      // 일괄 상태 변경
      bulkStatusModal: false,
      bulkStatus: '',
      bulkStatusError: null,
      bulkStatusSaving: false,
      // 파일 업로드 (관리자 증빙서류 추가)
      fileUploadModal: false,
      editingFiles: [],
      fileIdSeq: 0,
      fileUploading: false,
      fileUploadError: null
    }
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase()
      const isDiscountFilter = this.statusFilter === 'DISCOUNT'
      let result = this.waitings.filter(w => {
        const matchSearch = !q ||
          w.CarNumber.toLowerCase().includes(q)
        const matchStatus = !this.statusFilter || isDiscountFilter || w.Status === this.statusFilter
        const matchDiscount = !this.discountFilter || (this.discountFilter === 'NONE' ? !w.DiscountSeq : w.DiscountSeq == this.discountFilter)
        const matchGroup = !this.groupFilter || 
          (this.groupFilter === 'NOGROUP' ? !w.GroupSeq : w.GroupSeq == this.groupFilter)
        const matchExistingUser = !this.existingUserFilter ||
          (this.existingUserFilter === 'EXISTING' && !!w.IsExistingUser) ||
          (this.existingUserFilter === 'NEW' && !w.IsExistingUser)
        return matchSearch && matchStatus && matchDiscount && matchGroup && matchExistingUser
      })
      
      // 할인 항목은 별도 필터링
      if (isDiscountFilter) {
        result = result.filter(w => w.DiscountSeq || w.DiscountName)
      } else if (this.statusFilter) {
        result = result.filter(w => w.Status === this.statusFilter)
      }

      if (this.sortBy === 'waitingNumber') {
        result.sort((a, b) => {
          const aNum = a.WaitingNumber || Infinity
          const bNum = b.WaitingNumber || Infinity
          return aNum - bNum
        })
      } else if (this.sortBy === 'applicationDate') {
        result.sort((a, b) => {
          const aDate = new Date(a.ApplicationDate || 0)
          const bDate = new Date(b.ApplicationDate || 0)
          return bDate - aDate // 최신순
        })
      }
      
      return result
    },
    lotteryCandidateCount() {
      return this.waitings.filter(w => w.Status === 'WAITING').length
    },
    lotteryCandidatePreviewText() {
      const count = this.lotteryCandidateCount
      return count === 0 ? '추첨대기 대상자가 없습니다.' : `${count}명` 
    },
    selectAllChecked: {
      get() {
        return this.filtered.length > 0 && this.filtered.every(w => this.checked[w.WaitingSeq])
      },
      set(val) {
        if (val) {
          this.filtered.forEach(w => {
            this.checked[w.WaitingSeq] = true
          })
        } else {
          this.filtered.forEach(w => {
            this.checked[w.WaitingSeq] = false
          })
        }
      }
    },
    selectAllLoserChecked: {
      get() {
        return this.losersToMove && this.losersToMove.length > 0 && this.losersToMove.every(loser => this.selectedLoserSeqs.includes(loser.WaitingSeq))
      },
      set(val) {
        if (!this.losersToMove) return
        if (val) {
          this.losersToMove.forEach(loser => {
            if (!this.selectedLoserSeqs.includes(loser.WaitingSeq)) {
              this.selectedLoserSeqs.push(loser.WaitingSeq)
            }
          })
        } else {
          this.losersToMove.forEach(loser => {
            const idx = this.selectedLoserSeqs.indexOf(loser.WaitingSeq)
            if (idx > -1) {
              this.selectedLoserSeqs.splice(idx, 1)
            }
          })
        }
      }
    }
  },
  watch: {
    'smsPaymentData.startDate': function () {
      this.updateSmsBillFromStartDate()
      // startDate를 변경하면 해당 월의 마지막일로 endDate 자동 설정
      const sd = new Date(this.smsPaymentData.startDate)
      if (!isNaN(sd.getTime())) {
        const lastDay = new Date(sd.getFullYear(), sd.getMonth() + 1, 0)
        this.smsPaymentData.endDate = this.formatDateForInput(lastDay)
      }
    },
    'smsPaymentData.endDate': function () {
      // endDate change does not change bill month, but ensure preview stays consistent
      this.updateSmsBillFromStartDate()
    },
    'smsPaymentData.expireDate': function () {
      // update preview message when expire date is changed
      try {
        this.appendExpireToSms({ ExpireDate: this.smsPaymentData.expireDate })
        // persist if payment already exists
        if (this.createdPayment && this.createdPayment.PaymentSeq) {
          axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.smsTarget.WaitingSeq}/payment-request`, {
            originalPrice: this.createdPayment.OriginalPrice || this.calculateAmount(this.smsTarget),
            paymentPrice: this.createdPayment.PaymentPrice || this.calculateAmount(this.smsTarget),
            startDate: this.smsPaymentData.startDate,
            endDate: this.smsPaymentData.endDate,
            billName: this.smsBillName,
            expireDate: this.smsPaymentData.expireDate
          }).then(() => {
            // refresh createdPayment info
          }).catch(err => console.warn('Failed to persist expireDate:', err))
        }
      } catch (e) {
        console.error(e)
      }
    },
    filterNewUser() {
      this.updateLoserSelectionByFilter()
    },
    filterExistingUser() {
      this.updateLoserSelectionByFilter()
    }
  },
  mounted() {
    this.initializeAreas()
  },
  methods: {
    maskName(name) {
      if (!name) return ''
      const s = String(name).trim()
      const len = s.length
      if (len === 1) return '*'
      if (len === 2) return s[0] + '*'
      return s[0] + '*' + s[len - 1]
    },
    maskTel(tel) {
      if (!tel) return ''
      const s = String(tel)
      const digits = s.replace(/\D/g, '')
      if (digits.length <= 4) return s
      const last4 = digits.slice(-4)
      const prefix = digits.slice(0, 3)
      return `${prefix}-****-${last4}`
    },
    maskCarNumber(carNumber) {
      if (!carNumber) return ''
      const s = String(carNumber).trim()
      if (s.length <= 2) return s
      // 뒷자리 2자리를 ** 로 변경
      return s.slice(0, -2) + '**'
    },
    async updateSmsBillFromStartDate() {
      if (!this.smsTarget) return
      try {
        const sd = new Date(this.smsPaymentData.startDate)
        if (isNaN(sd.getTime())) return
        const y = sd.getFullYear()
        const m = sd.getMonth() + 1
        const newBill = `${this.smsTarget.AreaName} ${y}년 ${m}월 정기권`
        const oldBill = this.smsBillName || ''
        this.smsBillName = newBill

        // Update smsMessage: prefer to preserve user edits after first line.
        // If message starts with '[정기권 신청]' or contains oldBill, replace it; otherwise prepend.
        if (!this.smsMessage) {
          this.smsMessage = `[정기권 신청] ${newBill} ${this.smsTarget.UserName}님, 결제금액은 ${this.calculateAmount(this.smsTarget)}원 입니다`
        } else if (this.smsMessage.startsWith('[정기권 신청]')) {
          // replace the bill name after the tag
          const parts = this.smsMessage.split('\n')
          const first = parts[0]
          // Replace any existing bill pattern in first line
          const replaced = first.replace(oldBill, newBill).replace(/\s{2,}/g, ' ')
          parts[0] = replaced
          this.smsMessage = parts.join('\n')
        } else if (oldBill && this.smsMessage.includes(oldBill)) {
          this.smsMessage = this.smsMessage.replace(oldBill, newBill)
        } else {
          this.smsMessage = `[정기권 신청] ${newBill} ${this.smsTarget.UserName}님, 결제금액은 ${this.calculateAmount(this.smsTarget)}원 입니다` + (this.smsMessage.includes('\n') ? '\n' + this.smsMessage.split('\n').slice(1).join('\n') : '')
        }

        // If a payment record already exists, persist updated billName/startDate via payment-request endpoint
        if (this.createdPayment && this.createdPayment.PaymentSeq) {
          try {
            await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.smsTarget.WaitingSeq}/payment-request`, {
              originalPrice: this.createdPayment.OriginalPrice || this.calculateAmount(this.smsTarget),
              paymentPrice: this.createdPayment.PaymentPrice || this.calculateAmount(this.smsTarget),
              startDate: this.smsPaymentData.startDate,
              endDate: this.smsPaymentData.endDate,
              billName: this.smsBillName,
              expireDate: this.smsPaymentData.expireDate
            })
            // Ensure sms preview includes expire info based on current expireDate
            this.appendExpireToSms({ ExpireDate: this.smsPaymentData.expireDate })
          } catch (err) {
            // Non-fatal: keep preview updated even if persist fails
            console.warn('Failed to persist updated billName for payment:', err)
          }
        }
      } catch (e) {
        console.error('updateSmsBillFromStartDate error:', e)
      }
    },
    updateBulkSmsExampleFromStartDate(exampleItem) {
      if (!exampleItem) return
      const sd = new Date(this.bulkSmsPaymentData.startDate)
      if (isNaN(sd.getTime())) return

      const y = sd.getFullYear()
      const m = sd.getMonth() + 1
      const billName = `${exampleItem.AreaName} ${y}년 ${m}월 정기권`
      this.bulkSmsBillName = billName

      // TODO: future SMS preview formatting logic can use bulkSmsPaymentData.startDate/endDate/expireDate
    },
    async initializeAreas() {
      await this.fetchAreas()
      await this.fetchDiscounts()
      if (this.areas.length > 0) {
        this.selectedArea = this.areas[0].AreaSeq
        await this.fetchWaiting()
      }
    },
    async fetchAreas() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-areas`)
        this.areas = res.data
      } catch {
        // 주차장 목록 로드 실패 시 콤보박스가 비어있음
      }
    },
    async fetchDiscounts() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/discount`)
        this.discounts = res.data
      } catch {
        this.discounts = []
      }
    },
    async fetchFees(areaSeq = null) {
      try {
        const targetAreaSeq = areaSeq !== null ? areaSeq : this.selectedArea
        if (!targetAreaSeq) {
          this.fees = []
          return
        }
        // areaSeq로 area 객체 찾기
        const selectedAreaObj = this.areas.find(a => a.AreaSeq == targetAreaSeq)
        if (!selectedAreaObj) {
          this.fees = []
          return
        }
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-fee?areaCode=${selectedAreaObj.AreaCode}`)
        this.fees = res.data
      } catch {
        this.fees = []
      }
    },
    async fetchGroups() {
      try {
        if (!this.selectedArea) return
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/waiting-group-quota?areaSeq=${this.selectedArea}`)
        this.groups = res.data
      } catch {
        this.groups = []
      }
    },
    openResetModal() {
      this.resetModal = true
      this.resetError = null
    },
    async doReset() {
      this.loadingReset = true
      this.resetError = null
      try {
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/reset`, {
          areaSeq: parseInt(this.selectedArea)
        })
        this.resetModal = false
        await this.fetchWaiting(true)
      } catch (e) {
        this.resetError = e.response?.data?.message || '초기화에 실패했습니다.'
      } finally {
        this.loadingReset = false
      }
    },
    async fetchWaiting(resetFilters = false) {
      if (!this.selectedArea) return
      this.loading = true
      this.loadError = null
      this.waitings = []
      if (resetFilters) {
        this.search = ''
        this.statusFilter = ''
        this.existingUserFilter = ''
        this.discountFilter = ''
        this.groupFilter = ''
      }
      this.checked = {}
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting?areaSeq=${this.selectedArea}`)
        this.waitings = res.data
        await this.fetchGroups()
        await this.fetchFees()
      } catch (e) {
        this.loadError = '대기 목록을 불러오지 못했습니다.'
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async openFileViewer(w) {
      this.fileViewerLoading = true
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting/files?waitingSeq=${w.WaitingSeq}`)
        const baseUrl = (process.env.VUE_APP_API_BASE || '').replace(/\/$/, '')
        this.viewerFiles = (res.data.files || []).map(f => {
          const url = f.url || ''
          const normalizedUrl = url.startsWith('/') ? `${baseUrl}${url}` : url
          return {
            ...f,
            url: normalizedUrl
          }
        })
        this.fileViewerWaitingSeq = w.WaitingSeq
        this.fileViewerModal = true
      } catch (e) {
        alert('파일 조회에 실패했습니다: ' + (e.response?.data?.message || e.message))
      } finally {
        this.fileViewerLoading = false
      }
    },
    openEditStatusModal(w) {
      this.editTarget = w
      this.editStatus = w.Status || ''
      this.editFeeSeq = w.FeeSeq || ''
      this.editDiscountSeq = w.DiscountSeq || ''
      this.editIsExistingUser = ''
      this.editError = null
      this.editStatusModal = true
    },
    async saveEditStatus() {
      if (!this.editTarget) return
      
      this.editSaving = true
      this.editError = null
      try {
        await axios.put(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.editTarget.WaitingSeq}/status`, {
          status: this.editStatus || null,
          feeSeq: this.editFeeSeq ? parseInt(this.editFeeSeq) : null,
          discountSeq: this.editDiscountSeq ? parseInt(this.editDiscountSeq) : null,
          isExistingUser: this.editIsExistingUser !== '' ? this.editIsExistingUser : null
        })
        this.editStatusModal = false
        await this.fetchWaiting(false)
      } catch (e) {
        console.error('Edit status error:', e)
        this.editError = e.response?.data?.message || '상태 변경에 실패했습니다.'
      } finally {
        this.editSaving = false
      }
    },
    // 파일 업로드 메서드들
    openFileUploadModal(target) {
      if (!target) return
      this.editingFiles = []
      this.fileUploadError = null
      this.fileUploadModal = true
    },
    closeFileUploadModal() {
      this.fileUploadModal = false
      // 파일 미리보기 URL 해제
      for (const f of this.editingFiles) {
        if (f.preview) URL.revokeObjectURL(f.preview)
      }
      this.editingFiles = []
      this.fileUploadError = null
    },
    triggerFileInput() {
      this.$refs.fileInput?.click()
    },
    onInputFiles(e) {
      const list = e.target.files
      this.handleFiles(list)
      e.target.value = ''
    },
    onDrop(e) {
      const list = e.dataTransfer.files
      this.handleFiles(list)
    },
    handleFiles(fileList) {
      if (this.editingFiles.length >= 5) {
        this.fileUploadError = '최대 5개까지만 선택할 수 있습니다.'
        return
      }
      for (const f of Array.from(fileList)) {
        // 중복 체크
        if (this.editingFiles.some(x => x.file.name === f.name && x.file.size === f.size)) continue
        
        // 파일 크기 체크 (10MB)
        if (f.size > 10 * 1024 * 1024) {
          this.fileUploadError = `${f.name}은 10MB 이상으로 업로드할 수 없습니다.`
          continue
        }
        
        // 파일 개수 체크
        if (this.editingFiles.length >= 5) {
          this.fileUploadError = '최대 5개까지만 선택할 수 있습니다.'
          break
        }
        
        const item = {
          id: this.fileIdSeq++,
          file: f,
          preview: null,
          progress: 0,
          status: 'pending'
        }
        if (f.type.startsWith('image/')) {
          item.preview = URL.createObjectURL(f)
        }
        this.editingFiles.push(item)
      }
    },
    removeFile(idx) {
      const it = this.editingFiles.splice(idx, 1)[0]
      if (it && it.preview) URL.revokeObjectURL(it.preview)
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let i = 0
      let v = bytes
      while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
      return v.toFixed((i === 0) ? 0 : 2) + ' ' + units[i]
    },
    async uploadEditingFiles() {
      if (!this.editingFiles.length || !this.editTarget) return
      this.fileUploading = true
      this.fileUploadError = null
      try {
        for (const fObj of this.editingFiles) {
          fObj.status = 'uploading'
          fObj.progress = 0
          const form = new FormData()
          form.append('files', fObj.file)
        
          await axios.post(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting-upload?waitingSeq=${this.editTarget.WaitingSeq}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (evt) => {
              if (evt.total) {
                fObj.progress = Math.round((evt.loaded / evt.total) * 100)
              }
            }
          })
          fObj.status = 'done'
          fObj.progress = 100
        }
        
        // 업로드 완료 후 리스트 새로고침
        await this.fetchWaiting(false)
        
        // 업로드 완료 후 파일 뷰어 열기
        await this.openFileViewerForWaiting(this.editTarget.WaitingSeq)
        this.closeFileUploadModal()
      } catch (err) {
        console.error('file upload error', err)
        const cur = this.editingFiles.find(x => x.status === 'uploading')
        if (cur) cur.status = 'error'
        this.fileUploadError = err.response?.data?.message || '파일 업로드에 실패했습니다.'
      } finally {
        this.fileUploading = false
      }
    },
    async openFileViewerForWaiting(waitingSeq) {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/parking-pass-waiting/files?waitingSeq=${waitingSeq}`)
        console.log('파일 조회 응답:', res.data)
        this.viewerFiles = res.data.files || []
        this.fileViewerWaitingSeq = waitingSeq
        this.fileViewerModal = true
      } catch (e) {
        console.error('Error loading files:', e)
        this.fileUploadError = '파일을 불러올 수 없습니다.'
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      // dateString 형식: "2026-06-23 10:30:45"
      return dateString.substring(0, 16).replace('T', ' ')
    },
    getStatusBadgeClass(status) {
      const classes = {
        'WAITING': 'bg-warning',
        'LOTTERY_COMPLETED': 'bg-success',
        'DocumentPending': 'bg-secondary',
        'SENDED_SMS': 'bg-info',
        'PAYMENT_COMPLTETED': 'bg-success',
        'CONFIRMED': 'bg-success'
      }
      return classes[status] || 'bg-secondary'
    },
    getStatusLabel(status) {
      const labels = {
        'WAITING': '추첨대기',
        'LOTTERY_COMPLETED': '추첨완료',
        'DocumentPending': '증빙대기',
        'SENDED_SMS': '문자발송',
        'PAYMENT_COMPLTETED': '결제완료',
        'CONFIRMED': '확정'
      }
      return labels[status] || status
    },
    exportToExcel() {
      if (this.filtered.length === 0) {
        alert('다운로드할 대기 신청이 없습니다.')
        return
      }

      const escapeXml = value => {
        const str = String(value ?? '')
        return str.replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&apos;')
      }

      const areaName = this.areas.find(a => String(a.AreaSeq) === String(this.selectedArea))?.AreaName || '주차장'
      const timestamp = new Date().toISOString().slice(0, 10)
      const fileName = `정기권대기목록_${areaName}_${timestamp}.xls`
      const columns = [60, 120, 100, 100, 80, 120, 100, 120, 80, 100, 160, 80]
      const headers = ['#', '이름', '차량번호', '전화번호', '신규/기존', '요금제', '그룹', '할인', '추첨순번', '상태', '신청일', '증빙서류 수']
      const rows = this.filtered.map(w => [
        w.WaitingSeq || '',
        w.UserName || '',
        this.maskCarNumber(w.CarNumber) || '',
        w.Tel || '',
        w.IsExistingUser ? '기존' : '신규',
        w.FeeName || '',
        w.GroupName || '-',
        w.DiscountName || '없음',
        w.WaitingNumber || '-',
        this.getStatusLabel(w.Status) || '-',
        this.formatDate(w.ApplicationDate),
        w.FileCount || 0
      ])

      const xml = `<?xml version="1.0"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
          xmlns:html="http://www.w3.org/TR/REC-html40">
  <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
    <Author>EFPark</Author>
    <Created>${new Date().toISOString()}</Created>
    <Version>16.00</Version>
  </DocumentProperties>
  <Styles>
    <Style ss:ID="Header">
      <Font ss:Bold="1"/>
      <Alignment ss:Horizontal="Center" ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
    <Style ss:ID="Default">
      <Alignment ss:Vertical="Center"/>
      <Borders>
        <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1"/>
        <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1"/>
      </Borders>
    </Style>
  </Styles>
  <Worksheet ss:Name="정기권대기목록">
    <Table>
      ${columns.map(width => `<Column ss:Width="${width}"/>`).join('')}
      <Row ss:StyleID="Header">
        ${headers.map(header => `<Cell><Data ss:Type="String">${escapeXml(header)}</Data></Cell>`).join('')}
      </Row>
      ${rows.map(row => `<Row>${row.map(cell => `<Cell><Data ss:Type="String">${escapeXml(cell)}</Data></Cell>`).join('')}</Row>`).join('')}
    </Table>
  </Worksheet>
</Workbook>`

      const blob = new Blob([xml], { type: 'application/vnd.ms-excel' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    getOriginalAmount(w) {
      return Math.floor(Number(w.Price || 0))
    },
    calculateAmount(w) {
      let amount = w.Price || 0
      if (w.DiscountRate) {
        amount = amount * (1 - w.DiscountRate / 100)
      }
      return Math.floor(amount)
    },
    formatDateTimeForSms(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      if (isNaN(d.getTime())) return ''
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${day} ${hh}:${mm}`
    },
    appendExpireToSms(payment) {
      if (!payment) return
      const expire = payment.ExpireDate || payment.expireDate || payment.Expire || null
      if (expire) {
        const fmt = this.formatDateTimeForSms(expire)
        if (!fmt) return
        // If smsMessage already contains 결제기한, replace it; otherwise append on new line
        if (this.smsMessage.includes('결제기한')) {
          // replace only the 결제기한 line (stop at newline) to preserve any following lines (예: 결제링크)
          this.smsMessage = this.smsMessage.replace(/결제기한:[^\n]*/m, `결제기한: ${fmt}`)
        } else {
          this.smsMessage = `${this.smsMessage}\n결제기한: ${fmt}`
        }
      } else {
        // payment exists but expire not provided -> indicate check needed
        if (!this.smsMessage.includes('결제기한')) {
          this.smsMessage = `${this.smsMessage}\n결제기한: 확인 필요`
        }
      }
    },
    ensurePaymentLink(payment) {
      if (!payment || !payment.PaymentSeq) return
      const clientBase = (process.env.VUE_APP_CLIENT_URL || 'https://pmpark.efmc.or.kr').replace(/\/$/, '')
      const paymentLink = `${clientBase}/payment?paymentSeq=${payment.PaymentSeq}`
      console.log('[SMS Preview] ensurePaymentLink before:', { smsMessage: this.smsMessage, paymentSeq: payment.PaymentSeq, paymentLink })
      if (this.smsMessage.includes('결제링크')) {
        // replace existing 결제링크 line
        this.smsMessage = this.smsMessage.replace(/결제링크:\s*.*$/m, `결제링크: ${paymentLink}`)
      } else {
        this.smsMessage = `${this.smsMessage}\n결제링크: ${paymentLink}`
      }
      console.log('[SMS Preview] ensurePaymentLink after:', { smsMessage: this.smsMessage })
    },
    async openSmsModal(w) {
      this.smsTarget = w
      const finalAmount = this.calculateAmount(w)
      // billName은 startDate 기준으로 생성 (예: "주차장명 2026년 7월 정기권")
      const today = new Date()
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      const billYear = nextMonth.getFullYear()
      const billMonth = nextMonth.getMonth() + 1
      const billName = `${w.AreaName} ${billYear}년 ${billMonth}월 정기권`

      // 메시지 포맷: BillName 및 가격(콤마 제거) 표기, 예: "10000원 입니다"
      const priceText = `${finalAmount}원 입니다`
      this.smsMessage = `[정기권 신청] ${billName} ${w.UserName}님, 결제금액은 ${priceText}`
      this.createdPayment = null
      this.smsBillName = billName

      // 기본 날짜 설정: 다음달 1일 ~ 다음달 마지막일
      const nextMonthLastDay = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0)
      this.smsPaymentData.startDate = this.formatDateForInput(nextMonth)
      this.smsPaymentData.endDate = this.formatDateForInput(nextMonthLastDay)
      // default expire: 이번 달 마지막일
      const currentMonthLastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      currentMonthLastDay.setHours(23, 59, 59, 0)
      this.smsPaymentData.expireDate = this.formatDateTimeForInput(currentMonthLastDay)
      
      this.smsError = null

      // 모달을 열기만 함 (결제요청은 sendSms에서 생성)
      this.smsModal = true
    },
    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDateTimeForInput(date) {
      const d = new Date(date)
      if (isNaN(d.getTime())) return ''
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hh}:${mm}`
    },
    async sendSms() {
      this.smsSending = true
      this.smsError = null
      try {
        // 유효성 검증
        if (!this.smsPaymentData.startDate || !this.smsPaymentData.endDate) {
          this.smsError = '시작일과 종료일을 입력해주세요.'
          return
        }

        const startDate = new Date(this.smsPaymentData.startDate)
        const endDate = new Date(this.smsPaymentData.endDate)
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          this.smsError = '시작일 또는 종료일 형식이 올바르지 않습니다.'
          return
        }
        if (endDate < startDate) {
          this.smsError = '종료일은 시작일 이후여야 합니다.'
          return
        }

        const originalAmount = this.getOriginalAmount(this.smsTarget)
        const paymentAmount = this.calculateAmount(this.smsTarget)
        // Recompute billName based on current startDate (user may have edited it)
        let billName = this.smsBillName || ''
        try {
          const sd = new Date(this.smsPaymentData.startDate)
          if (!isNaN(sd.getTime())) {
            const y = sd.getFullYear()
            const m = sd.getMonth() + 1
            billName = `${this.smsTarget.AreaName} ${y}년 ${m}월 정기권`
            this.smsBillName = billName
            // If smsMessage does not already include billName, prepend it for consistency
            if (!this.smsMessage.includes(billName)) {
              // keep user-edited message body after a newline
              this.smsMessage = `[정기권 신청] ${billName} ${this.smsTarget.UserName}님, 결제금액은 ${paymentAmount}원 입니다` + (this.smsMessage.includes('\n') ? '\n' + this.smsMessage.split('\n').slice(1).join('\n') : '')
            }
          }
        } catch (e) {
          // ignore and proceed
        }
        // Step 0: 결제요청 생성(또는 기존 조회)
        let payment = null
        let isNewPayment = false
        console.log('[Payment Request] 저장 요청:', {
          waitingSeq: this.smsTarget.WaitingSeq,
          originalPrice: originalAmount,
          paymentPrice: paymentAmount,
          startDate: this.smsPaymentData.startDate,
          endDate: this.smsPaymentData.endDate
        })
        const payRes = await axios.post(
          `${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.smsTarget.WaitingSeq}/payment-request`,
          {
            originalPrice: originalAmount,
            paymentPrice: paymentAmount,
            startDate: this.smsPaymentData.startDate,
            endDate: this.smsPaymentData.endDate,
            billName: this.smsBillName || billName,
            expireDate: this.smsPaymentData.expireDate || null
          }
        )
        console.log('[Payment Request] 저장 결과:', payRes.data)
        isNewPayment = payRes.data.isNew || false
        payment = payRes.data.payment || null
        this.createdPayment = payment
        if (payment && payment.ExpireDate) {
          this.smsPaymentData.expireDate = this.formatDateTimeForInput(payment.ExpireDate)
        }
        // Ensure payment link is present in the message and append expire info
        if (payment && payment.PaymentSeq) {
          this.ensurePaymentLink(payment)
          this.appendExpireToSms(payment)
        }

        // Step 1: /api/ebpp/send를 통해 문자 발송
        console.log('[SMS] 요청 정보:', {
          phoneNumber: this.smsTarget.Tel,
          msg: this.smsMessage,
          url: `${process.env.VUE_APP_API_BASE}/api/ebpp/send`
        })
        console.log(`[SMS] 발송 대상: waitingSeq=${this.smsTarget.WaitingSeq}, userID=${this.smsTarget.UserID}`)
        const response = await axios.post(`${process.env.VUE_APP_API_BASE}/api/ebpp/send`, {
          phoneNumber: this.smsTarget.Tel,
          msg: this.smsMessage
        })
        
        // 문자 발송 성공 로그
        console.log('[SMS] 발송 응답:', response.data)
        
        // Step 2: 정기권 대기 상태를 SENDED_SMS로 변경
        try {
          console.log(`[SMS] 상태 변경 요청: WaitingSeq=${this.smsTarget.WaitingSeq}`)
          await axios.put(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.smsTarget.WaitingSeq}/status`, {
            status: 'SENDED_SMS'
          })
          console.log('[SMS] 상태 변경 완료: SENDED_SMS')
        } catch (statusErr) {
          console.error('[SMS] 상태 변경 실패:', statusErr)
          // 문자는 발송됐으므로 계속 진행
        }
        
        // 성공 메시지 표시
        const paymentLink = payment && payment.PaymentSeq 
          ? `${process.env.VUE_APP_API_BASE.replace(/\/api$/, '')}/payment?paymentSeq=${payment.PaymentSeq}`
          : '결제 링크 없음'
        const statusText = isNewPayment ? '신규 생성' : '기존 사용'
        const messageText = `✓ ${this.smsTarget.UserName}님에게 문자가 발송되었습니다.\n이용기간: ${this.smsPaymentData.startDate} ~ ${this.smsPaymentData.endDate}\n\n[결제 정보]\nPaymentSeq: ${payment?.PaymentSeq || 'N/A'}\n상태: ${statusText}\n결제 링크: ${paymentLink}`
        alert(messageText)
        this.smsModal = false
        
        // 목록 새로고침
        await this.fetchWaiting(false)
      } catch (e) {
        console.error('[SMS] 오류:', e)
        
        // 상세 에러 정보 수집
        let errorDetail = ''
        if (e.response) {
          errorDetail += `[HTTP ${e.response.status}] ${e.response.statusText}\n`
          if (e.response.data?.message) {
            errorDetail += `메시지: ${e.response.data.message}\n`
          }
          if (e.response.data?.error) {
            errorDetail += `에러: ${e.response.data.error}\n`
          }
          if (e.response.data?.detail) {
            errorDetail += `상세: ${e.response.data.detail}\n`
          }
          if (e.response.data) {
            errorDetail += `\n전체 응답:\n${JSON.stringify(e.response.data, null, 2)}`
          }
        } else if (e.request) {
          errorDetail = `[네트워크 오류] 서버로부터 응답이 없습니다.\n요청: ${e.request}`
        } else {
          errorDetail = `[클라이언트 오류] ${e.message}`
        }
        
        this.smsError = errorDetail || '처리 중 오류가 발생했습니다.'
      } finally {
        this.smsSending = false
      }
    },
    openPaymentModal(w) {
      this.paymentTarget = w
      this.paymentError = null
      this.paymentModal = true
    },
    async completePayment() {
      this.paymentProcessing = true
      this.paymentError = null
      try {
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/complete-payment`, {
          waitingSeq: this.paymentTarget.WaitingSeq
        })
        this.paymentModal = false
        await this.fetchWaiting(false)
      } catch (e) {
        this.paymentError = e.response?.data?.message || '결제 완료 처리에 실패했습니다.'
      } finally {
        this.paymentProcessing = false
      }
    },
    async openPaymentDetail(w) {
      this.paymentDetailTarget = w
      this.paymentDetailsList = []
      this.paymentDetailError = null
      this.paymentDetailModal = true
      
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/payment-request/list/${w.WaitingSeq}`)
        this.paymentDetailsList = res.data || []
      } catch (e) {
        this.paymentDetailError = e.response?.data?.message || '청구서 조회 실패'
      }
    },
    closePaymentDetail() {
      this.paymentDetailModal = false
      this.paymentDetailTarget = null
      this.paymentDetailsList = []
      this.paymentDetailError = null
    },
    async completePaymentManually(payment) {
      this.paymentDetailProcessing = true
      this.paymentDetailError = null
      
      try {
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/payment-request/complete-manual`, {
          paymentSeq: payment.PaymentSeq,
          waitingSeq: this.paymentDetailTarget.WaitingSeq
        })
        
        // 팝업 닫고 목록 새로고침
        this.closePaymentDetail()
        await this.fetchWaiting(false)
        alert('결제 처리가 완료되었습니다.')
      } catch (e) {
        this.paymentDetailError = e.response?.data?.message || '결제 완료 처리 실패'
      } finally {
        this.paymentDetailProcessing = false
      }
    },
    async deletePaymentRequest(payment) {
      this.paymentDetailProcessing = true
      this.paymentDetailError = null
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/payment-request/${payment.PaymentSeq}`)
        this.closePaymentDetail()
        await this.fetchWaiting(false)
        alert('청구서가 삭제되었습니다.')
      } catch (e) {
        this.paymentDetailError = e.response?.data?.message || '청구서 삭제에 실패했습니다.'
      } finally {
        this.paymentDetailProcessing = false
      }
    },
    async confirmDelete(w) {
      this.deleteTarget = w
      this.deleteError = null
      this.deletePaymentRequests = []
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/payment-request/list/${w.WaitingSeq}`)
        this.deletePaymentRequests = res.data || []
      } catch (e) {
        console.warn('delete modal payment requests load failed:', e)
        this.deletePaymentRequests = []
      }
      this.deleteModal = true
    },
    async doDelete() {
      this.saving = true
      this.deleteError = null
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/${this.deleteTarget.WaitingSeq}`)
        this.deleteModal = false
        await this.fetchWaiting(false)
      } catch (e) {
        console.error('Admin delete error:', e)
        this.deleteError = e.response?.data?.message || '삭제에 실패했습니다.'
        if (this.deleteError.includes('운영 중 정기권')) {
          this.deleteError = '운영 중 정기권에서 먼저 삭제해 주세요. 해당 신청은 현재 활성 정기권에 연결되어 있습니다.'
        }
      } finally {
        this.saving = false
      }
    },
    getSelectedAreaName() {
      const area = this.areas.find(a => a.AreaSeq === parseInt(this.selectedArea))
      return area ? area.AreaName : ''
    },
    getSelectedArea() {
      return this.areas.find(a => a.AreaSeq === parseInt(this.selectedArea)) || null
    },
    getSelectedAreaCapacity() {
      const area = this.getSelectedArea()
      return area ? area.Capacity : 0
    },
    getWaitingNumberBadgeClass(waitingNumber) {
      const capacity = this.getSelectedAreaCapacity()
      return waitingNumber <= capacity ? 'bg-success' : 'bg-secondary'
    },
    openLotteryModal() {
      // 그룹별 추첨 정보 초기화 (모든 그룹 표시)
      this.lotteryGroups = this.groups
        .sort((a, b) => a.GroupSeq - b.GroupSeq) // GroupSeq 순서
        .map(g => {
          // 해당 그룹의 WAITING 상태 대기자 수 계산
          const candidates = this.waitings.filter(w => w.GroupSeq === g.GroupSeq && w.Status === 'WAITING')
          return {
            GroupSeq: g.GroupSeq,
            GroupName: g.GroupName,
            QuotaCount: g.QuotaCount,
            candidateCount: candidates.length,
            progress: 0,
            status: 'idle',
            error: null,
            winners: []
          }
        })
      
      this.lotteryModal = true
      this.lotteryError = null
      this.lotterySuccess = null
    },
    closeLotteryModal() {
      this.lotteryModal = false
      this.lotteryGroups = []
      this.lotteryError = null
      this.lotterySuccess = null
    },
    async doGroupLottery(group) {
      group.isProcessing = true
      group.error = null
      
      // 대기자가 없는 그룹은 스킵
      if (group.candidateCount === 0) {
        group.status = 'skipped'
        group.progress = 100
        group.isProcessing = false
        return
      }
      
      group.status = 'processing'
      group.progress = 0
      
      try {
        // 그룹별 추첨 API 호출
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/lottery`, {
          areaSeq: parseInt(this.selectedArea),
          groupSeq: group.GroupSeq
        })
        
        group.status = 'completed'
        group.progress = 100
        
        // 추첨 완료 후 데이터 새로고침
        await this.fetchWaiting()
      } catch (e) {
        group.status = 'error'
        group.error = e.response?.data?.message || '추첨 중 오류가 발생했습니다.'
      } finally {
        group.isProcessing = false
      }
    },
    async showLotteryWinners(group) {
      this.selectedLotteryGroupForWinners = group
      this.lotteryWinnersModal = true
      this.lotteryWinnersLoading = true
      this.lotteryWinnersError = null
      this.lotteryWinners = []
      
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/lottery-winners`, {
          params: {
            areaSeq: parseInt(this.selectedArea),
            groupSeq: group.GroupSeq
          }
        })
        this.lotteryWinners = res.data.winners || []
      } catch (e) {
        this.lotteryWinnersError = e.response?.data?.message || '당첨자 조회 중 오류가 발생했습니다.'
      } finally {
        this.lotteryWinnersLoading = false
      }
    },
    async showWaitingList(group) {
      this.selectedGroupForWaitingList = group
      this.waitingListModal = true
      this.waitingListLoading = true
      this.waitingListError = null
      this.waitingList = []
      
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/group-waiting-list`, {
          params: {
            areaSeq: parseInt(this.selectedArea),
            groupSeq: group.GroupSeq
          }
        })
        this.waitingList = res.data.waitingList || []
      } catch (e) {
        this.waitingListError = e.response?.data?.message || '대기자 조회 중 오류가 발생했습니다.'
      } finally {
        this.waitingListLoading = false
      }
    },
    updateLoserSelectionByFilter() {
      // 필터 조건에 맞는 사람들만 자동으로 선택
      this.selectedLoserSeqs = []
      if (!this.losersToMove || !Array.isArray(this.losersToMove)) {
        return
      }
      this.losersToMove.forEach(loser => {
        const isNew = !loser.IsExistingUser
        const isExisting = loser.IsExistingUser
        
        const shouldSelect = (this.filterNewUser && isNew) || (this.filterExistingUser && isExisting)
        if (shouldSelect) {
          this.selectedLoserSeqs.push(loser.WaitingSeq)
        }
      })
    },
    async openLoserMoveModalFromLottery(group) {
      this.selectedGroupForLoserMove = group
      this.loserMoveModal = true
      this.loserMoveLoading = true
      this.loserMoveError = null
      this.losersToMove = []
      this.selectedLoserMoveGroup = ''
      this.selectedLoserSeqs = []
      this.filterNewUser = false
      this.filterExistingUser = true
      
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/lottery-losers`, {
          params: {
            areaSeq: parseInt(this.selectedArea),
            groupSeq: group.GroupSeq
          }
        })
        this.losersToMove = res.data.losers || []
        // 필터 기본값(기존만)에 맞는 사람들 자동 선택
        this.updateLoserSelectionByFilter()
      } catch (e) {
        this.loserMoveError = e.response?.data?.message || '미당첨자 조회 중 오류가 발생했습니다.'
      } finally {
        this.loserMoveLoading = false
      }
    },
    async openLoserMoveModal() {
      this.selectedGroupForLoserMove = this.selectedLotteryGroupForWinners
      this.loserMoveModal = true
      this.loserMoveLoading = true
      this.loserMoveError = null
      this.losersToMove = []
      this.selectedLoserMoveGroup = ''
      this.selectedLoserSeqs = []
      this.filterNewUser = false
      this.filterExistingUser = true
      
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/lottery-losers`, {
          params: {
            areaSeq: parseInt(this.selectedArea),
            groupSeq: this.selectedLotteryGroupForWinners.GroupSeq
          }
        })
        this.losersToMove = res.data.losers || []
        // 필터 기본값(기존만)에 맞는 사람들 자동 선택
        this.updateLoserSelectionByFilter()
      } catch (e) {
        this.loserMoveError = e.response?.data?.message || '미당첨자 조회 중 오류가 발생했습니다.'
      } finally {
        this.loserMoveLoading = false
      }
    },
    async doMoveLoserGroup() {
      if (!this.selectedLoserMoveGroup) {
        this.loserMoveError = '이동할 그룹을 선택하세요.'
        return
      }
      
      if (this.selectedLoserSeqs.length === 0) {
        this.loserMoveError = '이동할 사용자를 선택하세요.'
        return
      }
      
      this.loserMoveSaving = true
      this.loserMoveError = null
      
      try {
        const movedCount = this.selectedLoserSeqs.length
        await axios.put(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/move-losers-group`, {
          areaSeq: parseInt(this.selectedArea),
          fromGroupSeq: this.selectedGroupForLoserMove.GroupSeq,
          toGroupSeq: parseInt(this.selectedLoserMoveGroup),
          waitingSeqs: this.selectedLoserSeqs
        })
        
        // 완료 메시지 표시
        const targetGroup = this.groups.find(g => g.GroupSeq === parseInt(this.selectedLoserMoveGroup))
        const targetGroupName = targetGroup ? targetGroup.GroupName : '선택한 그룹'
        alert(`✅ ${movedCount}명의 미당첨자가 "${targetGroupName}" 그룹으로 이동되었습니다.\n\n이제 "${targetGroupName}" 그룹에서 추첨을 다시 진행하세요.`)
        
        // 미당첨자 모달 닫기
        this.loserMoveModal = false
        
        // 미당첨자 그룹 데이터 초기화
        this.losersToMove = []
        this.selectedLoserSeqs = []
        this.filterNewUser = false
        this.filterExistingUser = true
        
        // 메인 추첨 모달의 그룹별 대기인원 새로고침
        await this.fetchWaiting()
        
        // lotteryGroups의 각 그룹별 candidateCount를 다시 계산 (UI 즉시 업데이트)
        this.lotteryGroups.forEach(group => {
          const candidates = this.waitings.filter(w => w.GroupSeq === group.GroupSeq && w.Status === 'WAITING')
          group.candidateCount = candidates.length
        })
      } catch (e) {
        this.loserMoveError = e.response?.data?.message || '미당첨자 그룹 이동 중 오류가 발생했습니다.'
      } finally {
        this.loserMoveSaving = false
      }
    },
    getCheckedCount() {
      return Object.values(this.checked).filter(v => v).length
    },
    toggleCheckAll() {
      // computed setter에서 처리됨
    },
    onEvidenceStatusUpdated() {
      this.fetchWaiting(false)
    },
    onGroupFilterChange() {
      // 그룹 선택 시 할인 필터는 항상 전체로 초기화
      this.discountFilter = ''
    },
    openMoveGroupModal() {
      if (this.getCheckedCount() === 0) {
        alert('이동할 신청건을 선택하세요.')
        return
      }
      this.selectedMoveGroup = ''
      this.moveGroupError = null
      this.moveGroupModal = true
    },
    openBulkStatusModal() {
      if (this.getCheckedCount() === 0) {
        alert('상태를 변경할 신청건을 선택하세요.')
        return
      }
      this.bulkStatus = ''
      this.bulkStatusError = null
      this.bulkStatusModal = true
    },
    async doBulkStatusChange() {
      if (!this.bulkStatus) {
        this.bulkStatusError = '변경할 상태를 선택하세요.'
        return
      }
      this.bulkStatusSaving = true
      this.bulkStatusError = null
      try {
        const waitingSeqs = Object.keys(this.checked).filter(k => this.checked[k]).map(k => parseInt(k, 10))
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/bulk-status`, {
          waitingSeqs,
          status: this.bulkStatus
        })
        this.bulkStatusModal = false
        this.checked = {}
        await this.fetchWaiting(false)
      } catch (e) {
        this.bulkStatusError = e.response?.data?.message || '일괄 상태 변경에 실패했습니다.'
      } finally {
        this.bulkStatusSaving = false
      }
    },
    async doMoveGroup() {
      if (!this.selectedMoveGroup) {
        this.moveGroupError = '그룹을 선택하세요.'
        return
      }
      this.moveGroupSaving = true
      this.moveGroupError = null
      try {
        const waitingSeqs = Object.keys(this.checked).filter(k => this.checked[k]).map(k => parseInt(k, 10))
        await axios.post(`${process.env.VUE_APP_API_BASE}/api/admin/parking-pass-waiting/move-group`, {
          waitingSeqs,
          groupSeq: parseInt(this.selectedMoveGroup, 10)
        })
        this.moveGroupModal = false
        this.checked = {}
        await this.fetchWaiting()
      } catch (e) {
        this.moveGroupError = e.response?.data?.message || '그룹 이동에 실패했습니다.'
      } finally {
        this.moveGroupSaving = false
      }
    },
    openBulkSmsModal() {
      if (this.getCheckedCount() === 0) {
        alert('문자를 보낼 신청건을 선택하세요.')
        return
      }
      this.cardSmsModalOpen = true
    },
    onCardSmsSent() {
      // 발송 완료 후 처리: 모달은 유지하고 목록만 갱신합니다.
      this.fetchWaiting(false)
    },
    onCashSmsSent() {
      // 발송 완료 후 처리: 모달은 유지하고 목록만 갱신합니다.
      this.fetchWaiting(false)
    },
    openBulkCashSmsModal() {
      if (this.getCheckedCount() === 0) {
        alert('문자를 보낼 신청건을 선택하세요.')
        return
      }
      this.cashSmsModalOpen = true
    }
  }
}
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-box {
  background: #fff;
  border-radius: 0.375rem;
  padding: 1.5rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  min-width: 320px;
  max-width: 500px;
}

table {
  font-size: 0.875rem;
}

.fw-500 {
  font-weight: 500;
}

.font-monospace {
  font-family: 'Courier New', monospace;
}

/* 드래그 드롭 스타일 */
.drop-area {
  border: 2px dashed #cfd8dc;
  border-radius: 8px;
  cursor: pointer;
  background: #fafafa;
  transition: background-color 0.2s;
}
.drop-area:hover {
  background: #f4f7f9;
}
.drop-area input {
  display: none;
}
.file-item {
  padding: 8px;
  border: 1px solid #eef2f4;
  border-radius: 6px;
}
.thumb {
  width: 64px;
  height: 48px;
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}
.thumb img {
  max-width: 100%;
  max-height: 100%;
  display: block;
}
</style>
